import React from 'react'
import { useTypedDispatch, useTypedSelector } from '../../../app/store'
import { CollaborativeChoice, VotingStatus, Vote } from '../types'
import { ClockIcon } from '@heroicons/react/24/outline'
import { castVote, updateCollaborativeChoice } from '../collaborationSlice'
import { addChoice } from '../../simulation/simulationSlice'
import { useCastVoteMutation } from '../../../app/services/collaborationApi'
import { livekitService } from '../services/livekitService'

type VotingPanelProps = {
  choices: CollaborativeChoice[]
  currentUserId: number
  onVote: (choiceId: string, decision: 'approve' | 'reject' | 'abstain') => void
}

const VotingPanel: React.FC<VotingPanelProps> = ({ choices, currentUserId, onVote }) => {
  const dispatch = useTypedDispatch()
  const { participants, currentRoom, pendingChoices } = useTypedSelector((state) => state.collaborationSlice)

  // API mutation for persisting votes to backend
  const [castVoteToBackend, { isLoading: isSubmittingVote }] = useCastVoteMutation()

  const handleVote = async (choiceId: string, decision: 'approve' | 'reject' | 'abstain') => {
    if (currentUserId == null) {
      console.warn('handleVote: currentUserId is null')
      return
    }

    // Find participant by userId - support both number and string comparison
    const currentParticipant = participants.find(p =>
      p.userId === currentUserId ||
      p.userId?.toString() === currentUserId?.toString()
    )

    if (!currentParticipant) {
      console.warn('handleVote: Could not find participant for userId:', currentUserId, 'in participants:', participants)
      return
    }

    const vote: Vote = {
      participantId: currentParticipant.id,
      decision,
      timestamp: new Date().toISOString()
    }

    // Update local Redux state immediately for responsive UI
    dispatch(castVote({ choiceId, vote }))

    // Send vote to backend API for persistence
    if (currentRoom?.id) {
      try {
        const response = await castVoteToBackend({
          roomId: currentRoom.id,
          choiceId,
          participantId: currentParticipant.id,
          decision
        }).unwrap()
        console.log('Vote successfully sent to backend')

        // Broadcast vote via LiveKit for real-time sync to other participants
        try {
          livekitService.sendVote(choiceId, decision, currentParticipant.id)
          console.log('📤 Vote broadcast via LiveKit')
        } catch (broadcastError) {
          console.error('Failed to broadcast vote via LiveKit:', broadcastError)
        }

        // Check if this vote resolved the choice (tipped the voting threshold)
        if (response.data?.resolved && response.data?.choice) {
          const resolvedChoice = response.data.choice
          console.log('🗳️ [VotingPanel] Vote resolved the choice! Status:', resolvedChoice.status)

          // Look up the FULL CollaborativeChoice from pendingChoices
          // The API only returns minimal choice data (id, text), but pendingChoices has the full item from proposal
          const fullChoice = pendingChoices.find(c => c.id === resolvedChoice.id)

          // Use the full choice data for broadcasting so other participants get complete item info
          const choiceToBroadcast = fullChoice ? { ...resolvedChoice, choice: fullChoice.choice } : resolvedChoice

          // Broadcast the resolved choice to other participants via LiveKit
          try {
            livekitService.sendChoiceResolution(choiceToBroadcast as unknown as Record<string, unknown>)
            console.log('📤 [VotingPanel] Broadcasted choice resolution to other participants')
          } catch (broadcastError) {
            console.error('Failed to broadcast choice resolution:', broadcastError)
          }

          // Update local Redux state with the resolved choice
          dispatch(updateCollaborativeChoice(resolvedChoice))

          // If the choice was APPROVED, execute it locally
          // (We won't receive our own LiveKit message, so we need to execute here)
          if (resolvedChoice.status === VotingStatus.APPROVED) {
            const choiceToExecute = fullChoice?.choice || resolvedChoice.choice

            if (choiceToExecute) {
              console.log('✅ [VotingPanel] Choice APPROVED by our vote! Executing locally:', choiceToExecute)
              dispatch(addChoice(choiceToExecute))
            } else {
              console.error('❌ [VotingPanel] No choice data found to execute!')
            }
          }
        }
      } catch (error) {
        console.error('Failed to send vote to backend:', error)
        // The local state is already updated, so the UI will still work
        // Could add error handling/retry logic here if needed
      }
    }

    onVote(choiceId, decision)
  }



  const getTimeRemaining = (deadline: string) => {
    const now = new Date()
    const end = new Date(deadline)
    const diff = end.getTime() - now.getTime()
    
    if (diff <= 0) {
      return { display: 'Expired', isUrgent: true }
    }
    
    const minutes = Math.floor(diff / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)
    
    const isUrgent = minutes < 1
    
    if (minutes > 0) {
      return { display: `${minutes}m ${seconds}s`, isUrgent }
    } else {
      return { display: `${seconds}s`, isUrgent }
    }
  }

  const getVoteProgress = (choice: CollaborativeChoice) => {
    const totalVotes = choice.votes.length
    const totalParticipants = participants.length
    const approveVotes = choice.votes.filter(v => v.decision === 'approve').length
    const rejectVotes = choice.votes.filter(v => v.decision === 'reject').length
    const abstainVotes = choice.votes.filter(v => v.decision === 'abstain').length

    return {
      totalVotes,
      totalParticipants,
      approveVotes,
      rejectVotes,
      abstainVotes,
      approvePercentage: totalParticipants > 0 ? (approveVotes / totalParticipants) * 100 : 0,
      rejectPercentage: totalParticipants > 0 ? (rejectVotes / totalParticipants) * 100 : 0,
      abstainPercentage: totalParticipants > 0 ? (abstainVotes / totalParticipants) * 100 : 0
    }
  }

  // Helper to get proposer name - look up from participants if not directly available
  const getProposerName = (choice: CollaborativeChoice): string => {
    // If the name is already populated, use it
    if (choice.proposedByName && choice.proposedByName.trim()) {
      return choice.proposedByName
    }

    // Try to find the proposer in participants by their ID
    const proposer = participants.find(p =>
      p.id === choice.proposedBy ||
      p.userId?.toString() === choice.proposedBy?.toString()
    )

    if (proposer) {
      return `${proposer.firstName} ${proposer.lastName}`.trim() || `User ${proposer.userId}`
    }

    // Fallback to showing the ID if we can't find the name
    return choice.proposedBy ? `User ${choice.proposedBy}` : 'Unknown'
  }

  // Helper to safely get choice text - handles undefined choice.choice
  const getChoiceText = (choice: CollaborativeChoice): string => {
    if (!choice.choice?.item) {
      return 'Untitled choice'
    }
    return choice.choice.item.text || choice.choice.item.actionSentence || choice.choice.item.planSentence || 'Untitled choice'
  }

  // Filter out choices with invalid/undefined choice data to prevent crashes
  const validChoices = choices.filter(c => c && c.choice)

  if (validChoices.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-center p-4">
        <div>
          <ClockIcon className="w-8 h-8 mx-auto text-gray-300 mb-1" />
          <p className="text-xs text-gray-500">No pending votes</p>
          <p className="text-xs text-gray-400">Votes will appear here when team members propose decisions</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="grid gap-3">
        {validChoices.map((choice) => {
          const progress = getVoteProgress(choice)
          // Find participant with flexible matching for both number and string IDs
          const currentParticipant = participants.find(p =>
            p.userId === currentUserId ||
            p.userId?.toString() === currentUserId?.toString()
          )
          const userVote = currentParticipant ? choice.votes.find(v => v.participantId === currentParticipant.id) : undefined
          const hasVoted = !!userVote
          const timeRemaining = getTimeRemaining(choice.votingDeadline)
          // Disable voting if already voted OR if actively submitting
          const isVotingDisabled = hasVoted || isSubmittingVote

          return (
            <div
              key={choice.id}
              className="border border-gray-200 rounded-lg p-3 bg-white shadow-sm"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-semibold text-gray-900 truncate">
                    {getChoiceText(choice)}
                  </h4>
                  <p className="text-xs text-gray-600 mt-1">
                    Proposed by {getProposerName(choice)}
                  </p>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                    choice.status === VotingStatus.PENDING 
                      ? 'bg-yellow-100 text-yellow-800'
                      : choice.status === VotingStatus.APPROVED
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {choice.status}
                  </div>
                </div>
              </div>

              {/* Vote Progress */}
              <div className="mb-2">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress: {progress.totalVotes}/{progress.totalParticipants}</span>
                  <span className={timeRemaining.isUrgent ? 'text-red-600 font-medium' : ''}>
                    {timeRemaining.display}
                  </span>
                </div>
                
                {/* Progress bars */}
                <div className="flex space-x-1 h-1">
                  <div 
                    className="bg-green-500 rounded"
                    style={{ width: `${progress.approvePercentage}%` }}
                  />
                  <div 
                    className="bg-red-500 rounded"
                    style={{ width: `${progress.rejectPercentage}%` }}
                  />
                  <div 
                    className="bg-gray-300 rounded"
                    style={{ width: `${progress.abstainPercentage}%` }}
                  />
                  <div 
                    className="bg-gray-100 rounded flex-1"
                  />
                </div>
                
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span className="text-green-600">✓ {progress.approveVotes}</span>
                  <span className="text-red-600">✗ {progress.rejectVotes}</span>
                  <span className="text-gray-600">~ {progress.abstainVotes}</span>
                </div>
              </div>

              {/* Voting buttons */}
              {/* NOTE: Connections should only be added in the Actions panel BEFORE proposing a decision,
                  not during the voting phase. This prevents UI layout issues and keeps voting simple. */}
              {choice.status === VotingStatus.PENDING && (
                <>
                  <div className="flex space-x-1 mb-2">
                    <button
                      onClick={() => handleVote(choice.id, 'approve')}
                      disabled={isVotingDisabled}
                      className={`flex-1 py-1 px-2 rounded text-xs font-medium transition-colors ${
                        userVote?.decision === 'approve'
                          ? 'bg-green-600 text-white'
                          : isVotingDisabled
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {isSubmittingVote ? '...' : '✓ Approve'}
                    </button>
                    <button
                      onClick={() => handleVote(choice.id, 'reject')}
                      disabled={isVotingDisabled}
                      className={`flex-1 py-1 px-2 rounded text-xs font-medium transition-colors ${
                        userVote?.decision === 'reject'
                          ? 'bg-red-600 text-white'
                          : isVotingDisabled
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                    >
                      {isSubmittingVote ? '...' : '✗ Reject'}
                    </button>
                    <button
                      onClick={() => handleVote(choice.id, 'abstain')}
                      disabled={isVotingDisabled}
                      className={`flex-1 py-1 px-2 rounded text-xs font-medium transition-colors ${
                        userVote?.decision === 'abstain'
                          ? 'bg-gray-600 text-white'
                          : isVotingDisabled
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {isSubmittingVote ? '...' : '~ Abstain'}
                    </button>
                  </div>
                </>
              )}

              {hasVoted && choice.status === VotingStatus.PENDING && (
                <div className="mt-2 text-xs text-center text-gray-600 bg-gray-50 py-1 rounded">
                  You voted: <span className="font-medium">{userVote?.decision}</span>
                </div>
              )}
            </div>
          )
        })}

      </div>
    </div>
  )
}

export default VotingPanel